import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { homeworkApi } from '../api/homeworkApi';
import { useAuth } from '../context/AuthContext';
import BottomNavbar from '../components/ui/BottomNavbar';
import TeacherLeaderboard from '../components/leaderboard/TeacherLeaderboard';

const Leaderboard = () => {
  const { user: currentUser } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const navigate = useNavigate();

  // Load leaderboard data for students
  useEffect(() => {
    // If user is a teacher, don't load student leaderboard data
    if (currentUser && currentUser.role === 'teacher') {
      setLoading(false);
      return;
    }
    
    const loadLeaderboard = async () => {
      try {
        // Fetch real leaderboard data from backend
        const response = await homeworkApi.getLeaderboard();
        // Ensure we're setting an array for students
        const leaderboardData = response.data?.leaderboard || response.data || [];
        setStudents(Array.isArray(leaderboardData) ? leaderboardData : []);
      } catch (error) {
        console.error('Error loading leaderboard:', error);
        // Set empty array if API call fails
        setStudents([]);
      } finally {
        setLoading(false);
      }
    };

    loadLeaderboard();
  }, [currentUser]);

  const closeDetails = () => {
    setShowDetails(false);
    setSelectedStudent(null);
  };

  const viewStudentDetails = (student) => {
    // Add rank to the student object for display
    const studentWithRank = { ...student };
    
    // If student is not already in leaderboard, rank will be 0
    const rankIndex = students.findIndex(s => s.studentId === student.studentId);
    if (rankIndex !== -1) {
      studentWithRank.rank = rankIndex + 1;
    } else {
      studentWithRank.rank = 0; // Not ranked yet
    }
    
    setSelectedStudent(studentWithRank);
    setShowDetails(true);
  };

  // If user is a teacher, render the teacher leaderboard component
  if (currentUser && currentUser.role === 'teacher') {
    return <TeacherLeaderboard />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-purple-100 to-blue-100">
        <div className="text-center">
          <div className="relative">
            <div className="w-24 h-24 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full animate-bounce flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl">🏆</span>
            </div>
            <div className="animate-pulse text-xl font-bold text-gray-700">Loading leaderboard...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-red-50 to-pink-50 p-4 sm:p-6 pb-20 md:pb-6 pt-16">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-yellow-500 via-red-500 to-pink-500 bg-clip-text text-transparent mb-2">
            🏆 Leaderboard
          </h1>
          <p className="text-gray-700 text-lg">Top performers this month</p>
        </div>
        
        {/* Current User Section */}
        {currentUser && (
          <div className="mb-6 bg-white rounded-2xl shadow-lg p-4">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg mr-4">
                  {currentUser.firstName?.charAt(0)}{currentUser.lastName?.charAt(0)}
                </div>
                <div>
                  <h2 className="font-bold text-gray-900 text-lg">{currentUser.firstName} {currentUser.lastName}</h2>
                  <p className="text-gray-600 text-sm">Your profile</p>
                </div>
              </div>
              <button 
                onClick={() => {
                  // Find current user in leaderboard data
                  const userInLeaderboard = students.find(s => s.studentId === currentUser._id);
                  if (userInLeaderboard) {
                    viewStudentDetails(userInLeaderboard);
                  } else {
                    // Create a minimal user object if not found in leaderboard
                    const minimalUser = {
                      studentId: currentUser._id,
                      studentName: `${currentUser.firstName} ${currentUser.lastName}`,
                      studentEmail: currentUser.email,
                      totalPoints: 0,
                      averageScore: 0,
                      completedAssignments: 0,
                      completedAssessments: 0,
                      submissions: [],
                      badges: []
                    };
                    viewStudentDetails(minimalUser);
                  }
                }}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 text-sm"
              >
                View My Details
              </button>
            </div>
          </div>
        )}
        
        {/* Summary Stats */}
        {students.length > 0 && (
          <div className="mb-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white p-3 rounded-xl shadow text-center">
              <p className="text-lg font-bold text-blue-600">{students.length}</p>
              <p className="text-xs text-gray-600">Total Students</p>
            </div>
            <div className="bg-white p-3 rounded-xl shadow text-center">
              <p className="text-lg font-bold text-green-600">{students.reduce((sum, s) => sum + s.completedAssignments + s.completedAssessments, 0)}</p>
              <p className="text-xs text-gray-600">Total Submissions</p>
            </div>
            <div className="bg-white p-3 rounded-xl shadow text-center">
              <p className="text-lg font-bold text-purple-600">{Math.round(students.reduce((sum, s) => sum + s.averageScore, 0) / students.length) || 0}%</p>
              <p className="text-xs text-gray-600">Avg Score</p>
            </div>
            <div className="bg-white p-3 rounded-xl shadow text-center">
              <p className="text-lg font-bold text-amber-600">{students.reduce((max, s) => s.totalPoints > max ? s.totalPoints : max, 0)}</p>
              <p className="text-xs text-gray-600">Highest Score</p>
            </div>
          </div>
        )}

        <div className="mb-6 p-4 bg-white rounded-2xl shadow-lg">
          <h2 className="text-lg font-bold text-gray-900 mb-2">How Scoring Works</h2>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-blue-50 p-2 rounded-lg text-center">
              <div className="text-lg mb-1">📝</div>
              <p className="text-xs">Auto-graded assignments</p>
            </div>
            <div className="bg-green-50 p-2 rounded-lg text-center">
              <div className="text-lg mb-1">⚡</div>
              <p className="text-xs">Instant feedback</p>
            </div>
            <div className="bg-purple-50 p-2 rounded-lg text-center">
              <div className="text-lg mb-1">🏆</div>
              <p className="text-xs">Automatic rankings</p>
            </div>
          </div>
        </div>

        {/* Podium Visualization */}
        <div className="flex justify-center items-end mb-8 space-x-2">
          {/* Second Place */}
          <div className="flex flex-col items-center">
            <div className="bg-gray-300 text-gray-800 font-bold py-2 px-3 rounded-t-lg w-16 text-center">
              <div className="text-lg">🥈</div>
              <div className="text-sm">{students[1]?.totalPoints || 0}</div>
            </div>
            <div className="w-16 h-24 bg-gradient-to-b from-gray-400 to-gray-500 rounded-b-lg flex items-center justify-center">
              <div className="text-white text-lg font-bold">{students[1]?.studentName?.charAt(0) || 'S'}</div>
            </div>
            <div className="mt-2 text-center">
              <h3 className="font-bold text-gray-900 text-xs">{students[1]?.studentName || 'Student'}</h3>
              <p className="text-gray-600 text-xs">2nd</p>
            </div>
          </div>

          {/* First Place */}
          <div className="flex flex-col items-center">
            <div className="bg-yellow-400 text-yellow-900 font-bold py-2 px-3 rounded-t-lg w-20 text-center">
              <div className="text-lg">🥇</div>
              <div className="text-sm">{students[0]?.totalPoints || 0}</div>
            </div>
            <div className="w-20 h-32 bg-gradient-to-b from-yellow-500 to-yellow-600 rounded-b-lg flex items-center justify-center">
              <div className="text-white text-lg font-bold">{students[0]?.studentName?.charAt(0) || 'S'}</div>
            </div>
            <div className="mt-2 text-center">
              <h3 className="font-bold text-gray-900 text-xs">{students[0]?.studentName || 'Student'}</h3>
              <p className="text-gray-600 text-xs">1st 🎉</p>
            </div>
          </div>

          {/* Third Place */}
          <div className="flex flex-col items-center">
            <div className="bg-amber-700 text-amber-100 font-bold py-2 px-3 rounded-t-lg w-14 text-center">
              <div className="text-lg">🥉</div>
              <div className="text-sm">{students[2]?.totalPoints || 0}</div>
            </div>
            <div className="w-14 h-20 bg-gradient-to-b from-amber-800 to-amber-900 rounded-b-lg flex items-center justify-center">
              <div className="text-white text-lg font-bold">{students[2]?.studentName?.charAt(0) || 'S'}</div>
            </div>
            <div className="mt-2 text-center">
              <h3 className="font-bold text-gray-900 text-xs">{students[2]?.studentName || 'Student'}</h3>
              <p className="text-gray-600 text-xs">3rd</p>
            </div>
          </div>
        </div>

        {/* Full Rankings List */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900">Full Rankings</h2>
          </div>
          
          <div className="divide-y divide-gray-200">
            {students.slice(3).map((student, index) => (
              <div key={student.studentId} className="px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold mr-3 text-sm">
                      {student.studentName?.charAt(0) || 'S'}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 text-sm">{student.studentName || 'Unknown Student'}</h3>
                      <p className="text-xs text-gray-600">
                        {student.averageScore || 0}% 
                        ({student.completedAssignments + student.completedAssessments} completed)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button 
                      onClick={() => viewStudentDetails(student)}
                      className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                    >
                      Details
                    </button>
                    <div className="text-right">
                      <p className="font-bold text-gray-900 text-sm">{(student.totalPoints || 0)} pts</p>
                      <p className="text-xs text-gray-600">#{index + 4}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link 
            to="/homework" 
            className="inline-block bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-bold py-3 px-8 rounded-full text-lg transition-all duration-300 transform hover:scale-110 shadow-lg hover:shadow-xl"
          >
            Start Homework to Earn Points
          </Link>
        </div>
      </div>

      {/* Student Details Modal */}
      {showDetails && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-bold text-gray-900">
                  {selectedStudent.studentName}&#39;s Performance
                </h2>
                <button 
                  onClick={closeDetails}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="mt-1 flex items-center space-x-3">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {selectedStudent.totalPoints} pts
                </span>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  {selectedStudent.averageScore}% avg
                </span>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  {selectedStudent.rank > 0 ? `#${selectedStudent.rank} rank` : 'Not ranked yet'}
                </span>
              </div>
            </div>
            
            <div className="p-4">
              <h3 className="text-lg font-bold text-gray-900 mb-3">All Homework Scores</h3>
              
              {selectedStudent.submissions && selectedStudent.submissions.length > 0 ? (
                <div className="space-y-3">
                  {selectedStudent.submissions.map((submission, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 text-sm truncate">{submission.title}</h4>
                          <div className="flex items-center mt-1">
                            <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 capitalize mr-2">
                              {submission.type}
                            </span>
                            <span className="text-xs text-gray-500">
                              {submission.submittedAt ? new Date(submission.submittedAt).toLocaleDateString() : 'N/A'}
                            </span>
                          </div>
                        </div>
                        <div className="text-right ml-2">
                          <p className="font-bold text-gray-900 text-sm">{submission.score}/{submission.maxScore}</p>
                          <p className="text-xs text-gray-600">{submission.percentage}%</p>
                        </div>
                      </div>
                      <div className="mt-2 flex justify-between items-center">
                        <div className="flex items-center">
                          {submission.percentage >= 80 ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Excellent
                            </span>
                          ) : submission.percentage >= 60 ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              Good
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Needs Improvement
                            </span>
                          )}
                        </div>
                        <button 
                          onClick={() => {
                            // Navigate to homework review page using homework ID instead of submission ID
                            if (submission.type === 'assignment') {
                              // Use homeworkId if available, otherwise fall back to id
                              // Make sure we're getting the string representation of the ID
                              const homeworkId = (submission.homeworkId && submission.homeworkId.toString()) || submission.id;
                              navigate(`/homework/${homeworkId}/review`);
                            } else {
                              // For assessments, we might want to show a different view
                              alert('Assessment details view coming soon!');
                            }
                          }}
                          // Only show review button for current user's submissions
                          className={`${currentUser && selectedStudent && selectedStudent.studentId === currentUser._id ? 'text-blue-600 hover:text-blue-800' : 'text-gray-400 cursor-not-allowed'} text-xs font-medium`}
                          // Disable button if not current user's submission
                          disabled={!(currentUser && selectedStudent && selectedStudent.studentId === currentUser._id)}
                        >
                          {currentUser && selectedStudent && selectedStudent.studentId === currentUser._id ? 'Review' : 'Not Available'}
                        </button>

                      </div>
                      
                      {/* Progress bar for visual representation */}
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                        <div 
                          className={`h-1.5 rounded-full ${submission.percentage >= 80 ? 'bg-green-500' : submission.percentage >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${submission.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-4xl mb-2">📝</div>
                  <p className="text-gray-600 text-sm">No homework submissions found.</p>
                  <p className="text-gray-500 text-xs mt-1">This student has not submitted any homework yet.</p>
                </div>
              )}
              
              <div className="mt-4 pt-3 border-t border-gray-200">
                <h4 className="font-medium text-gray-900 text-sm mb-2">Performance Summary</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-600">Total Points</p>
                    <p className="font-bold text-gray-900 text-lg">{selectedStudent.totalPoints}</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-600">Avg Score</p>
                    <p className="font-bold text-gray-900 text-lg">{selectedStudent.averageScore}%</p>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-600">Assignments</p>
                    <p className="font-bold text-gray-900 text-lg">{selectedStudent.completedAssignments}</p>
                  </div>
                  <div className="bg-amber-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-600">Assessments</p>
                    <p className="font-bold text-gray-900 text-lg">{selectedStudent.completedAssessments}</p>
                  </div>
                </div>
              </div>
              
              {/* Badges section */}
              {selectedStudent.badges && selectedStudent.badges.length > 0 && (
                <div className="mt-4 pt-3 border-t border-gray-200">
                  <h4 className="font-medium text-gray-900 text-sm mb-2">Achievements</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedStudent.badges.map((badge, index) => (
                      <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        🏅 {badge}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-gray-200">
              <button 
                onClick={closeDetails}
                className="w-full py-2 px-4 bg-gray-200 text-gray-800 font-medium rounded-lg hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      <BottomNavbar />
    </div>
  );
};

export default Leaderboard;